import axios from 'axios';
import {createIntl, createIntlCache} from 'react-intl';
import {selectIsInstrumentOperational} from 'stores/selectors/instrumentSelectors';
import {fastInvokeAPIPromise, fastStartHubPromise, fastSilentInvokeAPIPromise} from '../../lib/fastSignalRHelper';
import messages from '../../localizations';
import {FastRobotErrorcode, SignalREvent, SignalRHub} from '../../constants/dataEnum';
import {setModalErrorCode} from '../actions/modalStateAction';
import GlobalReduxStore from '../globalReduxStore';

let axiosCancelToken = null;

export const cancelAxiosRequest = (msg) => {
  axiosCancelToken?.cancel(msg);
};

export const callAPI = async (
  method,
  url,
  route,
  data,
  params,
  responseType,
  onUploadProgress,
  rawResponse = false,
) => {
  let response;
  const reconnectLimit = 4;
  // axios cancel token
  axiosCancelToken = axios.CancelToken.source();

  for (let i = 0; i <= reconnectLimit; i += 1) {
    try {
      if (i > 0) {
        // waiting to call again
        // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      // eslint-disable-next-line no-await-in-loop
      response = await axios({
        method,
        url: `${url}/${route}`,
        data,
        params,
        responseType,
        onUploadProgress,
        cancelToken: axiosCancelToken.token,
      });

      console.group('axios');
      console.log('host', url);
      console.log('route', route);
      console.log('method', method);
      console.log('data', data);
      console.log('params', params);
      console.log('responseType', responseType);
      console.log('response', response.data);
      console.groupEnd();

      console.info(`call API ${url}/${route} success`);
      break;
    } catch (err) {
      console.error(`call API ${url}/${route} Error:`, err);
      if (axios.isCancel(err)) {
        console.log('Request canceled', err);
        break;
      } else if (err.response || (!err.response && i === reconnectLimit)) {
        throw err;
      } else {
        console.log('trying to reconnect...');
      }
    }
  }

  return rawResponse ? response : response?.data;
};

export const startSignalRHub = (hubName) => async (dispatch, getState) => {
  const {configState} = getState();
  const {hubHost} = configState;

  let hubUrl;
  let result;

  switch (hubName) {
    case SignalRHub.AUTHORING:
    case SignalRHub.LABWARE_DEFINITION:
    case SignalRHub.TRAY:
    case SignalRHub.USER_MANAGEMENT:
    case SignalRHub.SESSION:
    case SignalRHub.STATE:
    case SignalRHub.PROTOCOL_MANAGEMENT:
    case SignalRHub.INSTRUMENT_MANAGEMENT:
      hubUrl = hubHost.urlAuthoringLocation;
      break;
    case SignalRHub.INSTRUMENT:
    case SignalRHub.PROTOCOL:
    case SignalRHub.SOFTWARE_UPDATER:
    case SignalRHub.BACKUP_AND_RESTORE:
      hubUrl = hubHost.urlInstrumentControlLocation;
      break;
    default:
      break;
  }
  console.log('hubUrl', hubUrl);
  console.log([hubName], hubName);

  try {
    result = await fastStartHubPromise(hubUrl, hubName);
    console.log(`${hubName} hub Connection started!`);
  } catch (err) {
    console.error(`start ${hubName} hub Error:`, err.message);
  }

  return result;
};

const doInvokeAPI =
  (isSilent, hubName, apiName, ...apiParam) =>
  async (dispatch, getState) => {
    const {signalrState} = getState();
    const isInstrumentOperational = selectIsInstrumentOperational(getState());
    const hub = signalrState[hubName];
    const isInstrumentHub = hubName === SignalRHub.INSTRUMENT;

    let response;
    let emergencyHub;

    if ((!hub && !isInstrumentHub) || (!hub && isInstrumentHub && isInstrumentOperational)) {
      try {
        emergencyHub = await dispatch(startSignalRHub(hubName));
      } catch (err) {
        throw response;
      }
    }

    const invokeFunc = isSilent ? fastSilentInvokeAPIPromise : fastInvokeAPIPromise;

    try {
      response = await invokeFunc(hub || emergencyHub, apiName, ...apiParam);
      console.info(`invoke ${apiName} success`);
    } catch (err) {
      console.error(`invoke ${apiName} api Error:`, err.message);
      throw response;
    } finally {
      if (emergencyHub) {
        emergencyHub.stop();
      }
    }

    return response;
  };

export const invokeAPI =
  (hubName, apiName, ...apiParam) =>
  async (dispatch) => {
    const response = await dispatch(doInvokeAPI(false, hubName, apiName, ...apiParam));
    if (response?.result || response?.errorCode) {
      await dispatch(setErrorCodeNumber(response?.result || response?.errorCode));
    }
    return response;
  };

export const silentInvokeAPI =
  (hubName, apiName, ...apiParam) =>
  async (dispatch) => {
    const response = await dispatch(doInvokeAPI(true, hubName, apiName, ...apiParam));

    return response;
  };

export const resolveIntl = () => {
  const cache = createIntlCache();
  const locale = 'en';
  const intl = createIntl({locale, messages: messages.en}, cache);

  return intl;
};

export const isIntlTextValueExists = (intlId) => {
  const intl = resolveIntl();
  const msg = intl.formatMessage({id: intlId});

  return !(msg === intlId);
};

export const getErrorNumber = async (fastErrorCode) => {
  if (
    !fastErrorCode ||
    typeof fastErrorCode !== 'string' ||
    (fastErrorCode && fastErrorCode === FastRobotErrorcode.NoError)
  )
    return 0;

  const {dispatch} = GlobalReduxStore.getStore();

  const firstLetterUppercase = fastErrorCode[0].toUpperCase();
  const errorCode = `${firstLetterUppercase}${fastErrorCode.substr(1)}`;

  const errorCodeNumber = await dispatch(
    invokeAPI(SignalRHub.INSTRUMENT, SignalREvent.GET_FAST_ERROR_CODE_NUMBER, errorCode),
  );

  console.log(SignalREvent.GET_FAST_ERROR_CODE_NUMBER, errorCodeNumber);

  return Math.abs(errorCodeNumber);
};

export const setErrorCodeNumber = (fastErrorCode, getErrorCodeNumberOnly) => async (dispatch) => {
  const errorCodeNumber = await getErrorNumber(fastErrorCode);

  if (!getErrorCodeNumberOnly) dispatch(setModalErrorCode(Math.abs(errorCodeNumber)));

  return Math.abs(errorCodeNumber);
};

export const getBaseURL = (repositoryName) => {
  const {configState} = GlobalReduxStore.getState();
  const baseURL = configState.hubHost[repositoryName];

  return baseURL || '';
};
